function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/web.dom.iterable.js";
import React, { useEffect, useRef, useMemo, useState } from 'react';
import proptypes from 'prop-types';
const {
  string,
  node,
  arrayOf,
  oneOfType,
  shape,
  func,
  bool
} = proptypes;
import deepmerge from 'deepmerge';
import QueryContext from "./QueryContext.js";
import { filterClient } from "./dataModel/filterClient.js";
import { extend } from "./dataModel/extend.js";
import { updateStorageAndClientCache } from "./util.js";
import DataProviderClient from "./DataProvider.client.js";
const walkChildrenTree = children => {
  return React.Children.toArray(children).map(child => {
    var _child$type, _child$props, _child$props$original, _child$props2;
    if ((_child$type = child.type) !== null && _child$type !== void 0 && _child$type.dataModel) {
      return child;
    }
    if ((_child$props = child.props) !== null && _child$props !== void 0 && (_child$props$original = _child$props.originalType) !== null && _child$props$original !== void 0 && _child$props$original.dataModel) {
      return child;
    }
    if ((_child$props2 = child.props) !== null && _child$props2 !== void 0 && _child$props2.children) {
      return walkChildrenTree(child.props.children);
    }
    return null;
  }).flat().filter(val => val);
};
export const getQueryAttributes = function (children) {
  let asChildren = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  let inherited = arguments.length > 2 ? arguments[2] : undefined;
  const childWrapper = asChildren ? walkChildrenTree(children) : children;
  const client = childWrapper.map(child => {
    var _child$props3;
    const type = ((_child$props3 = child.props) === null || _child$props3 === void 0 ? void 0 : _child$props3.originalType) || child.type || {};
    return asChildren ? type.dataModel || {} : child;
  }).reduce((acc, cur) => {
    return deepmerge(acc, cur);
  }, inherited !== null && inherited !== void 0 && inherited.client ? inherited === null || inherited === void 0 ? void 0 : inherited.client : {});
  const server = childWrapper.map(child => {
    var _child$props4;
    const type = ((_child$props4 = child.props) === null || _child$props4 === void 0 ? void 0 : _child$props4.originalType) || child.type || {};
    return asChildren ? type.dataModel || {} : child;
  }).reduce((acc, cur) => {
    const filtered = filterClient(cur);
    return deepmerge(acc, filtered);
  }, inherited !== null && inherited !== void 0 && inherited.server ? inherited === null || inherited === void 0 ? void 0 : inherited.server : {});
  return {
    client,
    server
  };
};
export const QueryProvider = _ref => {
  let {
    dataSource,
    children,
    defaultVariables,
    mounted,
    skip,
    queryOptions,
    merge,
    cacheKey,
    persist,
    fusion,
    forceClientQueriesOnServer,
    inherit
  } = _ref;
  const dataStore = useRef({});
  const defaultVariablesRef = useRef({});
  const queryAttributeRef = useRef(null);
  const [persistReady, setPersistReady] = useState(false);
  if (defaultVariables) {
    defaultVariablesRef.current = defaultVariables;
  }

  // get all dataModels of children for client and server
  const queryAttributes = useMemo(() => {
    var _children$type;
    if (cacheKey && QueryProvider.cache[cacheKey] && Object.keys(QueryProvider.cache[cacheKey]).length) {
      return QueryProvider.cache[cacheKey];
    }
    if (queryAttributeRef.current) {
      return queryAttributeRef.current;
    }

    // merge + inherit + dynamic imports is not working properly
    // serverside should be adding the attribute from the dynamic import
    // (bathRenovation) but because the order it does not

    // cacheKey is different
    // needs to merge with the parent?
    // perhaps inherit should copy the attributes back to the parent

    if (merge && (_children$type = children.type) !== null && _children$type !== void 0 && _children$type.dataModel && cacheKey) {
      Object.keys(merge).forEach(key => {
        const queries = merge[key].queries.map(query => {
          var _QueryProvider$cache$;
          if (children.type.dataModel[query]) {
            return children.type.dataModel[query];
          }
          if (inherit && (_QueryProvider$cache$ = QueryProvider.cache[inherit].client) !== null && _QueryProvider$cache$ !== void 0 && _QueryProvider$cache$[query]) {
            return QueryProvider.cache[inherit].client[query];
          }
          return null;
        }).filter(val => val);
        // if inherit, need to go into
        // QueryProvider.cache[inherit].client(client contains all possible attributes)[query]
        // and add those to queries.  this will extend the merged (productClientOnlyProduct for example)
        if (queries.length > 1) {
          // eslint-disable-next-line no-param-reassign
          children.type.dataModel[key] = extend(...queries);
        }
      });
    }
    const inherited = inherit && QueryProvider.cache[inherit] ? QueryProvider.cache[inherit] : {
      client: {},
      server: {}
    };
    const attr = getQueryAttributes(children, true, inherited);

    // merged queries can be merged with inherited query provider data
    // in this case its necessary to clear the cache of the query
    // because it will be stale from the parent.
    if (inherit && merge && cacheKey) {
      Object.keys(merge).forEach(key => {
        const queries = merge[key].queries || [];
        queries.push(key);
        queries.forEach(query => {
          var _attr$client, _attr$server;
          if ((_attr$client = attr.client) !== null && _attr$client !== void 0 && _attr$client[query]) {
            delete attr.client[query]._cachedArgs;
            delete attr.client[query]._cachedQuery;
          }
          if ((_attr$server = attr.server) !== null && _attr$server !== void 0 && _attr$server[query]) {
            delete attr.server[query]._cachedArgs;
            delete attr.server[query]._cachedQuery;
          }
        });
      });
    }
    queryAttributeRef.current = attr;
    if (cacheKey) {
      var _children$type2, _children$type3;
      if (!inherit || inherit && (_children$type2 = children.type) !== null && _children$type2 !== void 0 && _children$type2.dataModel) {
        QueryProvider.cache[cacheKey] = attr;
      }

      // update the parent cache to use the same queries
      if (inherit && (_children$type3 = children.type) !== null && _children$type3 !== void 0 && _children$type3.dataModel) {
        QueryProvider.cache[inherit] = attr;
      }
    }
    return attr;
  }, [cacheKey]);
  const isClientResolved = _ref2 => {
    var _queryAttributes$clie;
    let {
      queryName
    } = _ref2;
    const clientResolved = (_queryAttributes$clie = queryAttributes.client[queryName]) === null || _queryAttributes$clie === void 0 ? void 0 : _queryAttributes$clie._isResolved;
    return !!clientResolved;
  };
  const setDefaultVariables = props => {
    defaultVariablesRef.current = _objectSpread(_objectSpread({}, defaultVariables.current), props);
  };
  useEffect(() => {
    if (!persist) return;
    if (typeof window !== 'undefined') {
      updateStorageAndClientCache(DataProviderClient);
      setTimeout(() => {
        setPersistReady(true);
      }, 0);
    }
  }, []);
  const value = {
    dataSource,
    defaultVariables: defaultVariablesRef,
    queryAttributes,
    mounted,
    skip,
    isClientResolved,
    queryOptions,
    merge,
    cacheKey,
    persist: {
      hasPersist: persist,
      ready: persistReady
    },
    setDefaultVariables,
    dataStore: fusion ? dataStore : null,
    forceClientQueriesOnServer
  };
  return /*#__PURE__*/React.createElement(QueryContext.Provider, {
    value: value
  }, children);
};
QueryProvider.propTypes = {
  children: oneOfType([node, arrayOf(node)]).isRequired,
  cacheKey: string.isRequired,
  dataSource: string,
  defaultVariables: shape({}),
  mounted: func,
  skip: func,
  queryOptions: func,
  persist: bool,
  merge: shape({
    [string]: shape({
      queries: arrayOf(string),
      fn: func
    })
  }),
  fusion: bool,
  forceClientQueriesOnServer: arrayOf(string),
  /**
   * inherit allows for extend parent query provider.
   * pass the cacheKey of the parent to extend it.
   */
  inherit: string
};
QueryProvider.defaultProps = {
  dataSource: '',
  defaultVariables: null,
  mounted: _ref3 => {
    let {
      mounted
    } = _ref3;
    return mounted;
  },
  persist: false,
  skip: _ref4 => {
    let {
      skip
    } = _ref4;
    return skip;
  },
  queryOptions: _ref5 => {
    let {
      options,
      queryName
    } = _ref5;
    return options;
  },
  merge: null,
  fusion: false,
  forceClientQueriesOnServer: [],
  inherit: null
};
QueryProvider.cache = {};
//# sourceMappingURL=QueryProvider.js.map